import type { OrgModelProjection } from '../models/OrgModelProjection';
import { orgProjectionCompletenessError } from '../services/orgProjectionCompleteness';

/** A stale draft is not a renderable preview. Its independently authorized
 * current graph can supply explicitly labelled context, never a draft or an
 * execution baseline. Other conflicts and failed/partial reads fail closed. */
export function mayShowCurrentOrgContext(input: {
  selected: boolean;
  scenarioError: unknown;
  discoveryError: unknown;
  currentError: unknown;
  currentModel: OrgModelProjection | undefined;
}): boolean {
  const error = input.scenarioError as { status?: number; code?: string } | null;
  return input.selected && error?.status === 409 && error.code === 'graph_version_conflict'
    && !input.discoveryError && !input.currentError && Boolean(input.currentModel)
    && !orgProjectionCompletenessError(input.currentModel);
}

/** Errors that revoke or invalidate the currently rendered permissioned view. */
export function orgProjectionErrorRequiresSceneClear(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const status = (error as { status?: unknown }).status;
  const code = (error as { code?: unknown }).code;
  return status === 401
    || status === 403
    || status === 404
    || code === 'ORG_PROJECTION_INCOMPLETE';
}

/**
 * A same-scope scene is the last authorized projection, so it may bridge a
 * pending replacement or a retryable/stale read. Revocation, disappearance,
 * incomplete topology, and scope changes still fail closed immediately.
 */
export function mayRetainOrgProjectionScene(input: {
  readonly hasScene: boolean;
  readonly sameScope: boolean;
  readonly error: unknown;
}): boolean {
  return input.hasScene
    && input.sameScope
    && !orgProjectionErrorRequiresSceneClear(input.error);
}
